import { addToast, Button, CircularProgress, Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { useSession } from "next-auth/react";
import { IconBriefcase2 } from "@tabler/icons-react";
import Image from "next/image";
import ReactMarkdown from "react-markdown";

interface Projeto {
  id_projeto: string;
  titulo: string;
  descricao: string;
  texto: string;
  imagem_atividade: string;
  tags?: string[];
}

export default function OpenViewProjectModal() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session } = useSession();

  const modal = searchParams.get("modal");
  const projectId = searchParams.get("projectId");
  const isOpen = modal === "viewProject";

  const [projeto, setProjeto] = useState<Projeto | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  const onOpenChange = (open: boolean) => {
    if (!open) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("modal");
      params.delete("projectId");

      const qs = params.toString();
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }
  };

  // Faz o GET quando o modal estiver aberto e tiver projectId na URL
  useEffect(() => {
    if (!isOpen || !projectId) return;

    const loadProjeto = async () => {
      try {
        setLoading(true);

        const res = await apiFetch(`/projetos/${projectId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.accessToken}`,
          },
        });

        const data = await res.json();

        if (!res.ok) {
          addToast({
            title: "Erro ao carregar o projeto",
            description: data.error,
            color: "danger",
          });
          return;
        }

        setProjeto(data);
      } finally {
        setLoading(false);
      }
    };

    loadProjeto();
  }, [isOpen, projectId, session?.accessToken]);

  const handleParticipar = async () => {
    if (!projeto) return;

    try {
      setIsLoadingButton(true);

      const res = await apiFetch(`/projetos/${projeto.id_projeto}/participar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.accessToken}`,
        },
        body: JSON.stringify({ role: "COLABORADOR" }),
      });

      const data = await res.json();

      if (res.ok) {
        addToast({
          title: "Participação enviada com sucesso",
          color: "success",
        });
      } else {
        addToast({
          title: "Erro ao participar do projeto",
          description: data.error,
          color: "danger",
        });
      }
    } finally {
      setIsLoadingButton(false);
    }
  };

  const hasValidImage = (url: any) => {
    if (!url) return false;
    if (typeof url !== "string") return false;

    const trimmed = url.trim();

    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") return false;

    try {
      new URL(trimmed);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} placement="center" backdrop="blur" className="md:min-w-1/2">
      <ModalContent className={`p-3 ${loading ? "flex justify-center items-center" : ""}`}>
        {(onClose) => (
          <>
            {loading ? (
              <div className="w-full flex justify-center items-center py-6">
                <CircularProgress size="lg" />
              </div>
            ) : (
              <>
                <ModalHeader>
                  <div className="w-full flex flex-col gap-2">
                    <h1 className="font-bold text-2xl">{projeto?.titulo}</h1>

                    <div className="flex items-center justify-start flex-row gap-2 text-sm md:text-base">
                      <IconBriefcase2 size={22} />
                      <p className="text-gray-700">{projeto?.descricao}</p>
                    </div>

                    {/* Hashtags */}
                    {projeto?.tags && projeto.tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {projeto.tags.map((tag, id) => (
                          <span key={id} className="px-3 py-1 text-xs md:text-sm rounded-full bg-black/5 text-gray-800 border border-black/10">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </ModalHeader>

                {/* limite vertical + sem scroll horizontal */}
                <ModalBody className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto overflow-x-hidden">
                  <div className="w-full">
                    {hasValidImage(projeto?.imagem_atividade) ? (
                      <Image
                        src={projeto!.imagem_atividade}
                        quality={100}
                        width={1200}
                        height={1000}
                        alt="imagem do projeto"
                        className="h-auto w-full rounded-xl"
                      />
                    ) : null}
                  </div>

                  {/* Markdown com quebra de linha garantida */}
                  <div
                    className={`
                      prose prose-neutral max-w-full
                      wrap-break-word whitespace-pre-wrap
                      **:wrap-break-word **:whitespace-pre-wrap
                      [&_pre]:whitespace-pre-wrap [&_pre]:wrap-break-word [&_pre]:overflow-x-auto
                    `}
                  >
                    <ReactMarkdown>{projeto?.texto ?? ""}</ReactMarkdown>
                  </div>

                  <div className="w-full flex justify-end gap-2">
                    <Button type="button" color="danger" onPress={onClose} isLoading={isLoadingButton}>
                      Fechar
                    </Button>
                    <Button type="button" color="success" onPress={handleParticipar} isLoading={isLoadingButton} isDisabled={!projeto}>
                      Participar
                    </Button>
                  </div>
                </ModalBody>
              </>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
